// Cadastrar camada — e, se preciso, o tema junto.
//
// O tema entra aqui em vez de ter tela própria porque ele só existe para
// agrupar camada: uma tela de "Temas" seria um CRUD que ninguém visita, e
// mandar a pessoa a outra tela no meio do cadastro é o jeito mais fácil de ela
// desistir. Quem cria a primeira camada de um assunto cria o assunto.
//
// A fonte **não** se cria aqui: ela tem licença, atribuição e declaração de
// dado pessoal, e enfiar isso num canto deste formulário convidaria a preencher
// por palpite — que é o que o ADR-008 existe para evitar.

import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { obter, type Conector, type Conta, type Fonte, type Projeto, type Tema } from "../api.js";
import { Gaveta } from "../pecas/basicos.js";
import { DescobertaWfs } from "../pecas/DescobertaWfs.js";

const NOVO = "__novo__";

export function NovaCamada({
  temas,
  fontes,
  conectores,
  contas,
  aoFechar,
  aoSalvar,
  salvando,
}: {
  temas: Tema[];
  fontes: Fonte[];
  conectores: Conector[];
  contas: Conta[];
  aoFechar: () => void;
  aoSalvar: (camada: Record<string, unknown>, temaNovo: Record<string, string> | null) => void;
  salvando: boolean;
}): ReactNode {
  const [nome, setNome] = useState("");
  const [temaId, setTemaId] = useState("");
  const [temaNome, setTemaNome] = useState("");
  const [endpoint, setEndpoint] = useState("");
  // Só o conector WFS tem parâmetro que a tela sabe preencher (correção 004).
  const [parametros, setParametros] = useState<Record<string, unknown>>({});

  // As três escolhas guardam `null` enquanto ninguém escolheu, e o valor
  // efetivo cai no primeiro da lista. `useState(lista[0]?.id)` parece o mesmo e
  // não é: o estado inicial é fixado na PRIMEIRA renderização, quando as listas
  // ainda estão vazias — e quando elas chegam, o select fica sem seleção e o
  // botão travado para sempre. Abrir a gaveta antes da resposta chegar é o
  // caminho normal, não o excepcional.
  const [temaEscolhido, setTema] = useState<string | null>(null);
  const [fonteEscolhida, setFonteId] = useState<string | null>(null);
  const [tipoEscolhido, setTipo] = useState<string | null>(null);

  // O dono NÃO segue o padrão dos três acima: aqui `""` é escolha legítima e
  // significa base pública — que é o caso de todas as camadas até hoje. Cair no
  // primeiro da lista poria o dado numa conta por acidente, e trocar o dono
  // depois não é possível (a rota recusa, como recusa trocar o conector).
  const [contaId, setContaId] = useState("");
  const [projetoId, setProjetoId] = useState("");

  const projetos = useQuery({
    queryKey: ["projetos", contaId],
    queryFn: () => obter<Projeto[]>(`/contas/${contaId}/projetos`),
    enabled: contaId !== "",
  });

  const tema = temaEscolhido ?? temas[0]?.id ?? NOVO;
  const fonteId = fonteEscolhida ?? fontes[0]?.id ?? "";
  const tipo = tipoEscolhido ?? conectores[0]?.tipo ?? "ogc";

  const criandoTema = tema === NOVO;
  const slugValido = /^[a-z][a-z0-9_]{1,40}$/.test(temaId);
  const pronto =
    nome.trim() !== "" &&
    fonteId !== "" &&
    (criandoTema ? slugValido && temaNome.trim() !== "" : true);

  const fonte = fontes.find((f) => f.id === fonteId);

  return (
    <Gaveta titulo="Nova camada" subtitulo="o que será ingerido e curado" aoFechar={aoFechar}>
      {fontes.length === 0 ? (
        <p className="nota-principio">
          Nenhuma fonte cadastrada. Uma camada precisa de fonte — é dela que vêm licença, atribuição
          e a declaração de dado pessoal. Cadastre a fonte primeiro, na tela de Fontes.
        </p>
      ) : (
        <>
          <div className="form-secao">
            <div className="form-secao-titulo">Identificação</div>
            <div className="campo">
              <label htmlFor="nome-camada">Nome</label>
              <input
                id="nome-camada"
                value={nome}
                placeholder="Imóveis certificados (SIGEF)"
                onChange={(e) => setNome(e.target.value)}
              />
            </div>
            <div className="campo">
              <label htmlFor="tema">Tema</label>
              <select id="tema" value={tema} onChange={(e) => setTema(e.target.value)}>
                {temas.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nome}
                  </option>
                ))}
                <option value={NOVO}>＋ novo tema…</option>
              </select>
            </div>

            {criandoTema ? (
              <>
                <div className="campo">
                  <label htmlFor="tema-id">Identificador do tema</label>
                  <input
                    id="tema-id"
                    value={temaId}
                    placeholder="fundiario"
                    onChange={(e) => setTemaId(e.target.value)}
                  />
                  {temaId !== "" && !slugValido ? (
                    <div className="campo-ajuda campo-ajuda-erro">
                      Minúsculas, dígitos e <code className="mono">_</code>, começando por letra.
                    </div>
                  ) : null}
                </div>
                <div className="campo">
                  <label htmlFor="tema-nome">Nome do tema</label>
                  <input
                    id="tema-nome"
                    value={temaNome}
                    placeholder="Fundiário"
                    onChange={(e) => setTemaNome(e.target.value)}
                  />
                </div>
              </>
            ) : null}
          </div>

          <div className="form-secao">
            <div className="form-secao-titulo">Procedência</div>
            <div className="campo">
              <label htmlFor="fonte">Fonte</label>
              <select id="fonte" value={fonteId} onChange={(e) => setFonteId(e.target.value)}>
                {fontes.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.orgao}
                    {f.publicavel ? "" : " (sem licença)"}
                  </option>
                ))}
              </select>
              {/* Descobrir isso na hora de publicar desperdiça a curadoria
                  inteira — o mesmo aviso que o card da camada já dá. */}
              {fonte && !fonte.publicavel ? (
                <div className="campo-ajuda campo-ajuda-atencao">
                  Esta fonte não tem licença e atribuição: dá para curar, não dá para publicar.
                </div>
              ) : null}
            </div>
          </div>

          <div className="form-secao">
            <div className="form-secao-titulo">Ingestão</div>
            <div className="campo">
              <label htmlFor="tipo">Tipo de conector</label>
              <select id="tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                {conectores.map((c) => (
                  <option key={c.tipo} value={c.tipo}>
                    {c.tipo} — {c.descricao}
                  </option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label htmlFor="endpoint">Endpoint</label>
              <input
                id="endpoint"
                value={endpoint}
                placeholder="https://…"
                onChange={(e) => setEndpoint(e.target.value)}
              />
              <div className="campo-ajuda">
                Opcional: deixe vazio para camada que só recebe arquivo.
              </div>
            </div>
            {tipo === "ogc" ? (
              <DescobertaWfs
                endpoint={endpoint}
                fonteId={fonteId || null}
                parametros={parametros}
                aoMudar={setParametros}
              />
            ) : null}
          </div>

          <div className="campo">
            <label htmlFor="conta">Dono do dado</label>
            <select
              id="conta"
              value={contaId}
              onChange={(e) => {
                setContaId(e.target.value);
                setProjetoId("");
              }}
            >
              <option value="">Base pública — curada pela 4Asset, compartilhada</option>
              {contas.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                  {c.slug === null ? " (sem slug — não pode publicar coleção)" : ""}
                </option>
              ))}
            </select>
            <p className="campo-ajuda">
              Base pública é o default e o caso de quase tudo. Escolher uma conta torna a camada{" "}
              <b>privada daquele cliente</b>, com isolamento no banco (ADR-020). Não dá para trocar
              depois: a camada teria versões seladas de um dono e passaria a ser de outro.
            </p>
          </div>

          {contaId !== "" ? (
            <div className="campo">
              <label htmlFor="projeto">Projeto</label>
              <select id="projeto" value={projetoId} onChange={(e) => setProjetoId(e.target.value)}>
                <option value="">Sem projeto</option>
                {(projetos.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </select>
              <p className="campo-ajuda">
                O projeto é a unidade de escopo de chave: uma credencial pode ser emitida sobre ele
                inteiro. Crie projetos na tela de Clientes.
              </p>
            </div>
          ) : null}

          <div className="acoes-form">
            <button
              className="btn btn-primario"
              disabled={!pronto || salvando}
              onClick={() =>
                aoSalvar(
                  {
                    nome: nome.trim(),
                    tema_id: criandoTema ? temaId : tema,
                    fonte_id: fonteId,
                    tipo_conector: tipo,
                    endpoint_url: endpoint.trim() || null,
                    // `parametros` só quando há o que mandar: o banco põe `{}`
                    // no lugar de `null`, e camada de arquivo não tem nenhum.
                    ...(tipo === "ogc" && Object.keys(parametros).length > 0 ? { parametros } : {}),
                    conta_id: contaId || null,
                    projeto_id: projetoId || null,
                  },
                  criandoTema ? { id: temaId, nome: temaNome.trim() } : null,
                )
              }
            >
              {salvando ? "Criando…" : "Criar camada"}
            </button>
            <button className="btn" onClick={aoFechar}>
              Cancelar
            </button>
          </div>
        </>
      )}
    </Gaveta>
  );
}
